import { ArrowUpRight } from "lucide-react";
import { Reveal, usePageMeta } from "@/components/site/Reveal";
import { DIVISIONS } from "@/lib/content";

export default function Divisions() {
  usePageMeta(
    "Divisions — SKN Group of Companies",
    "Six divisions of SKN Group: Home Decor (HAG Carpets), Footwear (Titas), Fragrances (Youvora, Ag Perfume), Real Estate (Arcadia), Tobacco Consumables, Investments & Securities."
  );

  return (
    <div data-testid="divisions-page">
      <section className="pt-40 md:pt-48 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">Our Divisions</p>
            <h1 className="font-display text-5xl md:text-7xl font-medium tracking-tight leading-[1.02] max-w-4xl">
              Sectoral strength, <span className="italic bronze">clarity of vision</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Six divisions, each a pillar of the Group — presented equally, built on
              nearly a century of entrepreneurial discipline.
            </p>
          </Reveal>
        </div>
      </section>

      {DIVISIONS.map((d, i) => (
        <section
          key={d.id}
          id={d.id}
          data-testid={`division-section-${d.id}`}
          className={`py-20 md:py-28 ${i % 2 === 1 ? "ink-section" : ""}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
              <Reveal>
                <figure className="overflow-hidden group">
                  <img
                    src={d.image}
                    alt={d.name}
                    className="w-full h-[380px] md:h-[460px] object-cover img-grade group-hover:scale-105 transition-transform duration-700"
                  />
                </figure>
              </Reveal>
            </div>
            <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : "md:col-start-8"}`}>
              <Reveal delay={0.1}>
                <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A]">
                  {String(i + 1).padStart(2, "0")} · {d.est}
                </p>
                <h2 className="mt-4 font-display text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">
                  {d.name}
                </h2>
                <p className={`mt-6 text-base leading-relaxed ${i % 2 === 1 ? "text-[#F7F5F0]/65" : "text-muted-foreground"}`}>
                  {d.description}
                </p>
                {d.companies.length > 0 ? (
                  <div className="mt-8 flex flex-wrap gap-3">
                    {d.companies.map((c) => (
                      <a
                        key={c.name}
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`division-company-link-${c.name.toLowerCase().split(" ")[0]}`}
                        className={`inline-flex items-center gap-2 px-5 py-3 text-sm border transition-colors duration-300 ${
                          i % 2 === 1
                            ? "border-[#F7F5F0]/30 text-[#F7F5F0] hover:border-[#B8997A] hover:text-[#B8997A]"
                            : "border-foreground/30 text-foreground hover:border-[#B8997A] hover:text-[#B8997A]"
                        }`}
                      >
                        {c.name}
                        {c.note && <span className="text-xs opacity-60">({c.note})</span>}
                        <ArrowUpRight size={14} strokeWidth={1.5} />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className={`mt-8 text-xs uppercase tracking-[0.25em] ${i % 2 === 1 ? "text-[#B8997A]" : "text-[#B8997A]"}`}>
                    The Group's investment &amp; trading division
                  </p>
                )}
              </Reveal>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
